import React from 'react';
import { Link } from 'react-router-dom';
import './Automation.css';

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ── Solution data ─────────────────────────────────────────────────────────────

interface Solution {
  id: string;
  title: string;
  tagline: string;
  image: string;
  imageAlt: string;
  features: string[];
}

const solutions: Solution[] = [
  {
    id: 'alimentos',
    title: 'Plantas de Alimentos',
    tagline: 'Dosificación precisa de ingredientes con trazabilidad completa por lote para la industria alimentaria.',
    image: '/images/tolva.jpg',
    imageAlt: 'Planta industrial de procesamiento de alimentos con dosificación automatizada',
    features: [
      'Indicador programable E-1310 para dosificación precisa',
      'Control inteligente del consumo de ingredientes',
      'Generación de reportes vía impresora o descarga PC',
      'Software "CATCHER" para reportes personalizados',
      'Trazabilidad completa de ingredientes por lote',
    ],
  },
  {
    id: 'checkweigher',
    title: 'Basculas de banda transportadora',
    tagline: 'Verificación automática de peso en movimiento integrada en sus líneas de producción.',    
    image: '/images/customers-manufacture.jpg',
    imageAlt: 'Sistema checkweigher para verificación de peso en línea de producción',
    features: [
      'Modo simple checkweigher o modo personalizado a su operación',
      'Pesaje de alta velocidad y precisión',
      'Clasificación automática por rangos de peso',
      'Construcción personalizada según necesidades industriales',
      'Almacenamiento de múltiples pesos de referencia',
      'Pistón neumático para rechazo automático de productos',
      'Integración con sistemas de control de calidad',
      'Reportes estadísticos de producción en tiempo real',
    ],
  },
  {
    id: 'ferroviario',
    title: 'Ferrocarrileras "WEIGH LINE"',
    tagline: 'Sistemas dinámicos y estáticos para pesaje de vagones ferroviarios con control logístico integrado.',
    image: '/images/automation-railway-scales.jpg',
    imageAlt: 'Sistema ferroviario WEIGH LINE para pesaje dinámico de vagones',
    features: [
      'Pesaje dinámico y estático de carros ferroviarios',
      'Seguimiento de pesos bruto, tara y neto',
      'Monitoreo de entrada y salida de mercancías',
      'Control automatizado de tráfico ferroviario',
      'Integración con sistemas de gestión logística',
    ],
  },
  {
    id: 'concreteras',
    title: 'Concreteras',
    tagline: 'Automatización de plantas de concreto con control preciso de dosificación de agregados y formulaciones.',
    image: '/images/concrete-mixer.jpg',
    imageAlt: 'Planta concretera con sistema de dosificación automatizado E-1310',
    features: [
      'Control de formulaciones de concreto a medida',
      'Dosificación automática de agregados y aditivos',
      'Generación de reportes de producción por turno',
      'Control de consumo de materias primas',
      'Programación de recetas personalizadas',
    ],
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const Automation: React.FC = () => {
  const total = String(solutions.length).padStart(2, '0');

  return (
    <div className="atm-page">

      {/* ── Hero ── */}
      <section className="atm-hero">
        <div className="container atm-hero-content">
          <span className="atm-hero-overline">Soluciones Industriales</span>
          <h1>Automatización Industrial<br />de Pesaje</h1>
          <p>
            Diseñamos e integramos sistemas a la medida: desde la dosificación de
            ingredientes hasta el control de vagones ferroviarios.
          </p>
        </div>
      </section>

      {/* ── Solutions ── */}
      <section className="atm-solutions">

        <div className="atm-solutions-intro">
          <span className="atm-label">Nuestras Soluciones</span>
          <h2 className="atm-section-title">Sistemas diseñados para cada proceso</h2>
          <p className="atm-section-sub">
            Más de 30 años diseñando sistemas automatizados de pesaje para los
            sectores más exigentes de la industria mexicana.
          </p>
        </div>

        {solutions.map((sol, i) => {
          const isReverse = i % 2 !== 0;
          return (
            <div
              key={sol.id}
              className={`atm-row-stripe${isReverse ? ' atm-row-stripe--alt' : ''}`}
            >
              <div className={`atm-row${isReverse ? ' atm-row--reverse' : ''}`}>

                {/* Photo panel */}
                <div className="atm-row-photo" role="img" aria-label={sol.imageAlt}>
                  <img src={sol.image} alt="" loading="lazy" />
                  <div className="atm-row-photo-vignette" aria-hidden="true" />
                </div>

                {/* Content panel */}
                <div className="atm-row-content">
                  <h3 className="atm-row-title">{sol.title}</h3>
                  <p className="atm-row-tagline">{sol.tagline}</p>
                  <ul className="atm-row-features">
                    {sol.features.map((f) => (
                      <li key={f}>
                        <span className="atm-row-check" aria-hidden="true">
                          <IconCheck />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          );
        })}
      </section>

      {/* ── Stats ── */}
      <section className="atm-stats">
        <div className="atm-stats-grid">
          <div className="atm-stat">
            <span className="atm-stat-num">5+</span>
            <span className="atm-stat-label">Tipos de solución</span>
          </div>
          <div className="atm-stat">
            <span className="atm-stat-num">+30</span>
            <span className="atm-stat-label">Años de experiencia</span>
          </div>
          <div className="atm-stat">
            <span className="atm-stat-num">A medida</span>
            <span className="atm-stat-label">Diseño de sistemas</span>
          </div>
          <div className="atm-stat">
            <span className="atm-stat-num">Nacional</span>
            <span className="atm-stat-label">Cobertura de servicio</span>
          </div>
        </div>
      </section>

      {/* ── Why us ── */}
      <section className="atm-why">
        <div className="container">
          <div className="atm-why-header">
            <span className="atm-label">Nuestra Diferencia</span>
            <h2 className="atm-section-title">¿Por qué confiar en nosotros?</h2>
            <p className="atm-section-sub">
              Combinamos experiencia en pesaje industrial, ingeniería de automatización
              y soporte técnico certificado para entregar soluciones que duran.
            </p>
          </div>

          <div className="atm-why-grid">

            <div className="atm-why-card">
              <div className="atm-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </div>
              <div className="atm-why-body">
                <h3>Diseño a la medida</h3>
                <p>Cada sistema es diseñado según los requerimientos específicos de su proceso productivo, no soluciones genéricas de catálogo.</p>
              </div>
            </div>

            <div className="atm-why-card">
              <div className="atm-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <div className="atm-why-body">
                <h3>Calibración certificada EMA</h3>
                <p>Todos nuestros sistemas integran equipos calibrados y verificados por laboratorio acreditado M70 ante la EMA.</p>
              </div>
            </div>

            <div className="atm-why-card">
              <div className="atm-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <path d="M8 21h8M12 17v4"/>
                </svg>
              </div>
              <div className="atm-why-body">
                <h3>Integración con ERP y SCADA</h3>
                <p>Conectamos el pesaje con sus sistemas de gestión empresarial para trazabilidad y reportes automáticos en tiempo real.</p>
              </div>
            </div>

            <div className="atm-why-card">
              <div className="atm-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/>
                  <line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <div className="atm-why-body">
                <h3>Soporte en toda la República</h3>
                <p>Técnicos propios en Guadalajara, CDMX, Monterrey y San Luis Potosí para instalación, puesta en marcha y mantenimiento.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="atm-cta">
        <div className="container">
          <h2>¿Listo para automatizar su proceso?</h2>
          <p>
            Cuéntenos sobre su proceso y nuestros ingenieros le propondrán la
            solución de automatización adecuada para su industria.
          </p>
          <div className="atm-cta-actions">
            <Link to="/contacto" className="btn-primary">Solicitar Información</Link>
            <a href="tel:+523336349050" className="btn-outline">Llamar: (33) 3634-9050</a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Automation;
